import { useState } from "react";
import {
  Plus,
  Search,
  Bookmark,
  FolderOpen,
  BookText,
  Clock,
  ChevronRight,
} from "lucide-react";
import { useWorkspaceMode } from "@/context/WorkspaceModeContext";

export const CONVERSATIONS = [
  {
    id: "CV-2145",
    section: "today",
    title: "Vestibular schwannoma & postop facial weakness",
    when: "10:12",
    provenance: 4,
  },
  {
    id: "CV-2144",
    section: "today",
    title: "Intentional residuals & six-month outcomes",
    when: "09:07",
    provenance: 5,
  },
  {
    id: "CV-2143",
    section: "today",
    title: "CPA tumor pre-case review — R.K.",
    when: "07:42",
    provenance: 6,
  },
  {
    id: "CV-2141",
    section: "yesterday",
    title: "Pterional vs orbitozygomatic for MCA aneurysm",
    when: "21:04",
    provenance: 3,
  },
  {
    id: "CV-2140",
    section: "yesterday",
    title: "Facial nerve monitoring changes · alarms review",
    when: "16:22",
    provenance: 4,
  },
  {
    id: "CV-2138",
    section: "prev-7",
    title: "Prior sphenoid-wing meningiomas with dural tail",
    when: "Tue",
    provenance: 5,
  },
  {
    id: "CV-2136",
    section: "prev-7",
    title: "Speech arrest during phase-2 awake mapping",
    when: "Mon",
    provenance: 4,
  },
  {
    id: "CV-2134",
    section: "prev-7",
    title: "Endocrine follow-up cadence post-transsphenoidal",
    when: "Sun",
    provenance: 3,
  },
  {
    id: "CV-2118",
    section: "saved",
    title: "Retrosigmoid closure — CSF-leak mitigation",
    when: "★",
    provenance: 6,
  },
  {
    id: "CV-2115",
    section: "case-linked",
    title: "NN-2036-E · MCA aneurysm — intra-op reasoning",
    when: "Case",
    provenance: 5,
  },
  {
    id: "CV-2103",
    section: "case-linked",
    title: "NN-2038-C · Right temporal GBM — mapping plan",
    when: "Case",
    provenance: 7,
  },
  {
    id: "CV-2090",
    section: "mm-linked",
    title: "M&M candidates · this month",
    when: "M&M",
    provenance: 4,
  },
];

const SECTIONS = [
  { key: "today",       label: "Today",                 Icon: Clock },
  { key: "yesterday",   label: "Yesterday",             Icon: Clock },
  { key: "prev-7",      label: "Previous 7 days",       Icon: Clock },
  { key: "saved",       label: "Saved conversations",   Icon: Bookmark },
  { key: "case-linked", label: "Case-linked",           Icon: FolderOpen },
  { key: "mm-linked",   label: "M&M-linked",            Icon: BookText },
];

export default function ConversationsSidebar({ activeId, onSelect, onNew }) {
  const [query, setQuery] = useState("");
  const { flashedConvId } = useWorkspaceMode();
  const filter = (c) =>
    !query.trim() || c.title.toLowerCase().includes(query.toLowerCase());

  return (
    <aside
      className="nn-glass-sidebar flex h-full w-[224px] shrink-0 flex-col p-3"
      role="navigation"
      aria-label="Recent conversations"
    >
      {/* New conversation button */}
      <button
        onClick={onNew}
        className="mb-2.5 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left transition-colors"
        style={{
          background: "linear-gradient(180deg, rgba(160,200,255,0.20), rgba(120,165,240,0.08))",
          border: "1px solid rgba(160,200,255,0.28)",
          color: "hsl(var(--nn-ink))",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
        }}
        data-testid="new-conversation"
      >
        <Plus className="h-3.5 w-3.5 text-[hsl(var(--nn-blue))]" strokeWidth={1.8} />
        <span className="text-[12px] font-medium">New conversation</span>
      </button>

      {/* Search */}
      <label
        className="mb-3 flex items-center gap-2 rounded-xl px-2.5 py-1.5"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <Search className="h-3 w-3 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.8} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 bg-transparent text-[11.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] focus:outline-none"
          placeholder="Search conversations"
          data-testid="search-conversations"
        />
      </label>

      {/* Section list */}
      <div className="nn-scroll -mx-1 flex-1 overflow-y-auto pr-1">
        {SECTIONS.map((s) => {
          const items = CONVERSATIONS.filter((c) => c.section === s.key).filter(filter);
          if (items.length === 0) return null;
          return (
            <div key={s.key} className="mb-3 last:mb-1">
              <div className="mb-1 flex items-center gap-1.5 px-2 text-[9.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                <s.Icon className="h-2.5 w-2.5" strokeWidth={1.8} />
                {s.label}
              </div>
              <ul className="flex flex-col gap-0.5">
                {items.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => onSelect(c.id)}
                      data-active={c.id === activeId || undefined}
                      data-flash={c.id === flashedConvId || undefined}
                      className="group nn-conv-item flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left"
                    >
                      <div className="min-w-0 flex-1 leading-tight">
                        <div className="truncate text-[11.5px] font-medium text-[hsl(var(--nn-ink))]">
                          {c.title}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[9.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                          <span>{c.when}</span>
                          <span>·</span>
                          <span>{c.provenance} sources</span>
                        </div>
                      </div>
                      <ChevronRight
                        className="mt-1 h-3 w-3 shrink-0 text-[hsl(var(--nn-ink-faint))] opacity-0 transition-opacity group-hover:opacity-100"
                        strokeWidth={1.8}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
